import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import equal from 'fast-deep-equal';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import { map, filter, shareReplay, switchMap, distinctUntilChanged, concatMap } from 'rxjs/operators';
import { Site } from 'src/app/api-module/control-centre-api-client';
import { PatchUserDetails, UserManagementApiClient } from 'src/app/api-module/user-management-api-client-v2';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { Org, User } from 'src/app/models';
import { ClientSite } from 'src/app/models/clientsite';
import { Constants } from 'src/app/models/constants';
import { SettingsService } from 'src/app/services/settings.service';
import { SnackbarService } from 'src/app/services/snackbar.sevice';
import { OrgSiteSelectionService } from './org-site-selection.service';
import { Tab } from '@app/models/tab';
import { Urls } from '@app/models/urls';

@Component({
    selector: 'app-org-site-selection',
    templateUrl: './org-site-selection.component.html',
    styleUrls: ['./org-site-selection.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class OrgSiteSelectionComponent implements OnInit, OnDestroy {
    hideSites = true;
    user: User;

    allowedOrgs$ = this.orgSiteSelectionService.allowedOrgs$;
    currentSelectedOrg$ = this.orgSiteSelectionService.getOrgSelected$();

    private readonly allowedSitesSource = new BehaviorSubject<ClientSite[]>(null);
    defaultSiteSource = new BehaviorSubject<ClientSite>(null);
    changingDefaultSiteSource = new BehaviorSubject<boolean>(false);
    currentSelectedSiteSource = new BehaviorSubject<ClientSite>(undefined);
    siteOptionsSource = new BehaviorSubject<ClientSiteOptions[]>([]);

    allowedSites$ = this.allowedSitesSource.asObservable();
    defaultSite$ = this.defaultSiteSource.asObservable();
    currentSelectedSite$ = this.currentSelectedSiteSource.asObservable();

    private readonly subscriptions = new Subscription();

    constructor(
        private readonly router: Router,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly authenticationService: AuthenticationService,
        private readonly settingsService: SettingsService,
        private readonly userManagementApiClient: UserManagementApiClient,
        private readonly snackbarService: SnackbarService
    ) {}

    ngOnInit(): void {
        // Org initialize
        this.subscriptions.add(
            this.authenticationService.currentUser
                .pipe(
                    concatMap(async user => {
                        if (!user?.orgs || user.orgs.size <= 0) {
                            return [];
                        }
                        this.user = user;
                        const orgs = Array.from(user.orgs.values()).sort((orgA, orgB) =>
                            orgA.orgCode.toLowerCase().localeCompare(orgB.orgCode.toLowerCase())
                        );
                        for (const org of orgs) {
                            try {
                                const client = await firstValueFrom(this.settingsService.getClientByOrg(org.orgCode));
                                org.clientName = client.clientName;
                                org.countryCode = client.countryCode;
                            } catch (exception) {
                                console.error('Error fetching client name and country code for ' + org.orgCode);
                                console.error(exception);
                            }
                        }
                        return orgs;
                    })
                )
                .pipe(
                    filter(orgs => orgs != null && orgs != undefined && orgs.length > 0),
                    shareReplay()
                )
                .subscribe(async orgs => {
                    this.orgSiteSelectionService.updateOrgs(orgs);
                    const currentOrgCode = this.orgSiteSelectionService.getOrgSelectedValue()?.orgCode;
                    if (!currentOrgCode) {
                        await this.selectOrg(orgs[0], false);
                    }
                })
        );

        // Sites initialize
        const sites$: Observable<Site[]> = this.currentSelectedOrg$.pipe(
            switchMap(org => {
                if (!org) {
                    return of([]);
                }
                return this.settingsService.getSitesByOrg(org.orgCode);
            })
        );

        this.subscriptions.add(
            // TODO -> CC-1447 -> to fix the issue of "When selecting a new org, the sites$ still fires with the list of sites from previous org."
            combineLatest([this.currentSelectedOrg$, sites$, this.orgSiteSelectionService.getTab()])
                .pipe(
                    filter(([org, sites, _tab]) => org != null && org != undefined && sites.length > 0),
                    map(([org, sites, tab]) => {
                        const clientSites = sites
                            .map(site => new ClientSite(site.id, site.siteName))
                            .sort((a, b) => a.siteName.localeCompare(b.siteName));
                        this.orgSiteSelectionService.updateAllSites(clientSites);
                        const allowedSites = OrgSiteSelectionComponent.filterAllowedSites(org, clientSites);
                        const result: [Org, ClientSite[], Tab] = [org, allowedSites, tab];
                        return result;
                    })
                )
                .pipe(
                    map(([org, sites, tab]) => {
                        this.orgSiteSelectionService.setHasOnlyOneSite(sites.length === 1);
                        if (sites.length > 1 && tab !== Tab.ImagingRequests) {
                            sites.unshift(new ClientSite(null, 'All'));
                        }
                        const result: [Org, ClientSite[]] = [org, sites];
                        return result;
                    })
                )
                .pipe(
                    filter(([org, sites]) => org != null && org != undefined && sites.length > 0),
                    distinctUntilChanged((prev, current) => equal(prev, current)),
                    shareReplay()
                )
                .subscribe(([org, clientSites]) => {
                    this.allowedSitesSource.next(clientSites);
                    this.orgSiteSelectionService.updateAllowedSites(clientSites, org.defaultSiteId);
                })
        );

        this.subscriptions.add(
            combineLatest([
                this.orgSiteSelectionService.getAllowedSites(),
                this.orgSiteSelectionService.getSitesSelected(),
                this.orgSiteSelectionService.getTab()
            ])
                .pipe(
                    filter(([[sites, _defaultSiteId], _selectedSiteIds, _tab]) => sites !== undefined && sites !== null && sites.length > 0) // Make sure sites available
                )
                .subscribe(([[sites, defaultSiteId], selectedSiteIds, tab]) => {
                    const defaultSite = defaultSiteId && sites.find(site => site.siteId === defaultSiteId);
                    if (defaultSite) {
                        this.defaultSiteSource.next(defaultSite);
                    }

                    if (selectedSiteIds && selectedSiteIds.length > 0) {
                        // If they've selected sites
                        if (selectedSiteIds.length > 1 && tab !== Tab.ImagingRequests) {
                            // But they've selected more than one, it's all sites (except on imaging request tab)
                            this.currentSelectedSiteSource.next(new ClientSite(null, 'All'));
                        } else {
                            // Set said site as selected
                            const site = sites.find(site => selectedSiteIds.includes(site.siteId));
                            this.currentSelectedSiteSource.next(site ?? sites[0]);
                        }
                    } else if (defaultSite) {
                        // If they have a valid default, select it
                        this.currentSelectedSiteSource.next(defaultSite);
                    } else if (sites.length > 1 && tab !== Tab.ImagingRequests) {
                        // If there's more than one site available, select all sites (except on imaging request tab)
                        this.currentSelectedSiteSource.next(new ClientSite(null, 'All'));
                    } else {
                        // If there's only one site, select that site
                        this.currentSelectedSiteSource.next(sites[0]);
                    }
                })
        );

        this.subscriptions.add(
            combineLatest([this.allowedSitesSource, this.currentSelectedSiteSource, this.defaultSiteSource])
                .pipe(
                    map(([allowedSites, selectedSite, favouriteSite]) => {
                        const siteOptions: ClientSiteOptions[] =
                            allowedSites?.map(site => {
                                const isFavourite = favouriteSite && favouriteSite.siteId == site.siteId;
                                return {
                                    ...site,
                                    isFavourite,
                                    isSelected: selectedSite && selectedSite.siteId == site.siteId,
                                    class: this.getSiteClass(site, isFavourite)
                                };
                            }) ?? [];
                        return siteOptions;
                    })
                )
                .subscribe(siteOptions => {
                    this.siteOptionsSource.next(siteOptions);
                })
        );

        this.determineShowSites(this.router);
    }

    private determineShowSites(router: Router) {
        router.events.forEach(event => {
            if (event instanceof NavigationEnd) {
                const currentURL = event['url'].split('?')[0];
                if (!currentURL.includes('/users') && currentURL !== Urls.DASHBOARD_DEMAND_MANAGEMENT) {
                    this.hideSites = false;
                } else {
                    this.hideSites = true;
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    async selectOrg(clientOrg: Org, forceSelect: boolean) {
        const orgWithSettings = await firstValueFrom(this.settingsService.getClientByOrg(clientOrg.orgCode, true));
        clientOrg.settings = orgWithSettings.settings;
        this.orgSiteSelectionService.selectOrg(clientOrg, forceSelect);
    }

    selectSites(client: ClientSite) {
        if (this.currentSelectedSiteSource.value?.siteId !== client.siteId) {
            if (client.siteName === 'All') {
                const siteIds = this.allowedSitesSource.value.map(site => site.siteId).filter(siteId => siteId);
                this.orgSiteSelectionService.selectSites(siteIds);
            } else {
                this.orgSiteSelectionService.selectSites([client.siteId]);
            }
        }
    }

    favouriteSite(client: ClientSite, $event: PointerEvent) {
        // We don't want favouriting to close the menu.
        $event.stopPropagation();

        if (this.changingDefaultSiteSource.value) {
            return;
        }

        const allowedSiteIds = this.allowedSitesSource.value.map(site => site.siteId);
        if (allowedSiteIds.includes(client.siteId)) {
            this.changingDefaultSiteSource.next(true);
            const oldFavourite = this.defaultSiteSource.value;
            if (oldFavourite?.siteId === client.siteId) {
                // We are removing a default site.
                client = null;
            }

            this.defaultSiteSource.next(client);

            const siteOrgCode = this.orgSiteSelectionService.getOrgSelectedValue().orgCode;
            if (siteOrgCode !== null) {
                this.changeDefaultSite(this.user.id, siteOrgCode, client?.siteId)
                    .then(async () => {
                        await this.authenticationService.notifyUserUpdated();
                        const updatedOrg = this.orgSiteSelectionService.getOrgs().find(org => org.orgCode === siteOrgCode);
                        await this.selectOrg(updatedOrg, true);
                    })
                    .catch(_ => {
                        this.snackbarService.openSnackBar(Constants.UnableToContactServerMessage, SnackbarService.SNACK_BAR_CLASS_ALERT);
                        this.defaultSiteSource.next(oldFavourite);
                    })
                    .finally(() => {
                        this.changingDefaultSiteSource.next(false);
                    });
            } else {
                // I don't expect this to happen until we are dealing with multi org users.
                this.snackbarService.openSnackBar(Constants.UserSettingError, SnackbarService.SNACK_BAR_CLASS_ALERT);
                this.defaultSiteSource.next(oldFavourite);
                this.changingDefaultSiteSource.next(false);
            }
        }
    }

    isFavouriteSite(clientSiteId: number) {
        return clientSiteId === this.defaultSiteSource.value?.siteId;
    }

    getSiteClass(client: ClientSite, isFavourite: boolean) {
        if (client.siteName === 'All') {
            return ' invisible';
        } else if (isFavourite) {
            return ' visible favourite';
        }
        return ' visible';
    }

    async changeDefaultSite(userId: string, orgCode: string, siteId: number | null) {
        const request = new PatchUserDetails();
        request.defaultSiteId = siteId ?? 0;
        return await this.userManagementApiClient.userOrgsV_UpdateUserDetails(userId, orgCode, request).toPromise();
    }

    /**
     * Returns an intersection of org's sites and user's allowed sites
     */
    static filterAllowedSites(org: Org, sites: ClientSite[]): ClientSite[] {
        // TODO: Include Role checking so roles can have different sites
        if (!org?.roles) {
            return [];
        }
        const { allowedSites, orgWide } = org.getAllowedSites();
        return sites.filter(clientSite => orgWide || allowedSites.indexOf(clientSite.siteId) !== -1);
    }
}

class ClientSiteOptions extends ClientSite {
    isFavourite: boolean;
    isSelected: boolean;
    class: string;
}
